import { getMatchedMeterSearchField, toMeterSearchData } from '@/shared/db/repositories/meter-search-record';
import type { CachedEntityRecord, FieldDatabase } from '@/shared/db/schema';
import {
  METER_IDENTIFIER_KINDS,
  type MeterSearchResult,
  type NormalizedMeterIdentifier,
} from '@/shared/meter-identification';

export class MeterSearchRepository {
  constructor(private readonly database: FieldDatabase) {}

  async seedIfEmpty(meters: CachedEntityRecord[]) {
    const count = await this.database.count('meters');

    if (count > 0) {
      return;
    }

    for (const meter of meters) {
      await this.database.put('meters', meter);
    }
  }

  async findByIdentifier(
    identifier: NormalizedMeterIdentifier,
    input: { locationId?: number | string } = {},
  ): Promise<MeterSearchResult[]> {
    if (!identifier.normalizedValue) {
      return [];
    }

    const meters = await this.database.getAll('meters');

    return meters.flatMap(meter => {
      const data = toMeterSearchData(meter);

      if (!data._id) {
        return [];
      }

      if (input.locationId && data.locationId !== input.locationId) {
        return [];
      }

      const matchedBy = getMatchedMeterSearchField(data, identifier);

      if (!matchedBy) {
        return [];
      }

      return [
        {
          id: meter.id,
          data,
          matchedBy,
        },
      ];
    });
  }

  async listCachedMeters(input: { locationId?: number | string } = {}): Promise<MeterSearchResult[]> {
    const meters = await this.database.getAll('meters');

    return meters.flatMap(meter => {
      const data = toMeterSearchData(meter);

      if (!data._id) {
        return [];
      }

      if (input.locationId && data.locationId !== input.locationId) {
        return [];
      }

      return [
        {
          data,
          id: meter.id,
          matchedBy: METER_IDENTIFIER_KINDS.unknown,
        },
      ];
    });
  }
}
